/**
 * wishlistStore — Manages active wishlists loaded into the 3D viewer.
 *
 * When a user taps "Browse Shop" from a WishListPage, the wishlist
 * is saved to localStorage (`dukkan_active_wishlists`).  When the viewer
 * opens for a shop, it loads any active wishlists and renders them as
 * color-coded read-only markers.
 *
 * Multiple wishlists from different people for the same shop are supported
 * (e.g., wife's + daughter's lists).
 */
import { create } from 'zustand'
import { apiUrl } from '@/config'
import { useAppStore } from '@/store/appStore'
import { removeWishlistToken } from '@/utils/wishlistHistory'

// Fixed color palette for wish markers (up to 6 people)
export const WISH_COLORS = [
  '#ec4899', // pink
  '#a855f7', // purple
  '#f59e0b', // amber
  '#10b981', // emerald
  '#06b6d4', // cyan
  '#f97316', // orange
] as const

export interface WishItem {
  name: string
  price: number | null
  frameIndex: number
  sectionId: string
  shopId: string
  shopName: string
  thumbnailUrl: string | null
  note: string
  imagePixelX: number
  imagePixelY: number
  radius: number
  point3D: [number, number, number] | null
}

export interface ActiveWishlist {
  token: string
  senderName: string
  title: string
  note: string
  shopId: string
  shopName: string
  color: string
  items: WishItem[]
  visible: boolean // toggle per-person visibility
}

interface WishlistState {
  /** All loaded wishlists for the current shop */
  activeWishlists: ActiveWishlist[]
  /** Loading state */
  loading: boolean
  /** Banner dismissed */
  bannerDismissed: boolean

  /** Load wishlists from localStorage for a given shopId */
  loadWishlistsForShop: (shopId: string) => Promise<void>
  /** Toggle visibility of a specific person's wishlist */
  toggleWishlistVisibility: (token: string) => void
  /** Remove a wishlist from active set */
  removeActiveWishlist: (token: string) => void
  /** Dismiss the legend banner */
  dismissBanner: () => void
  /** Clear all */
  clearAll: () => void
}

// localStorage key for saved wishlist references
const STORAGE_KEY = 'dukkan_active_wishlists'

/** Saved reference in localStorage (lightweight — no item data) */
interface SavedWishlistRef {
  token: string
  senderName: string
  title: string
  shopId: string
}

/** Save a wishlist reference for a shop (called from WishListPage "Browse Shop") */
export function saveActiveWishlistRef(ref: SavedWishlistRef): void {
  try {
    const all = getActiveWishlistRefs()
    // De-duplicate by token
    const updated = [ref, ...all.filter((r) => r.token !== ref.token)]
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
  } catch { /* ignore */ }
}

/** Remove a saved reference */
export function removeActiveWishlistRef(token: string): void {
  try {
    const updated = getActiveWishlistRefs().filter((r) => r.token !== token)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
  } catch { /* ignore */ }
}

/** Get all saved references */
export function getActiveWishlistRefs(): SavedWishlistRef[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

export const useWishlistStore = create<WishlistState>((set) => ({
  activeWishlists: [],
  loading: false,
  bannerDismissed: false,

  loadWishlistsForShop: async (shopId: string) => {
    // shopId here is the dataset ID from the viewer (e.g. 'vid204414').
    // Saved refs may store the real shop DB UUID instead — resolve both
    // so we match regardless of which form was saved.
    const { availableDatasets } = useAppStore.getState()
    const dataset = availableDatasets.find((d) => d.id === shopId)
    const realShopId = dataset?.shopId || shopId

    const refs = getActiveWishlistRefs().filter(
      (r) => r.shopId === shopId || r.shopId === realShopId
    )
    if (refs.length === 0) {
      set({ activeWishlists: [], loading: false })
      return
    }

    set({ loading: true, bannerDismissed: false })

    const loaded: ActiveWishlist[] = []
    for (let i = 0; i < refs.length; i++) {
      const ref = refs[i]
      try {
        const res = await fetch(apiUrl(`/api/wishlists/${ref.token}`))
        if (!res.ok) continue
        const data = await res.json()
        loaded.push({
          token: ref.token,
          senderName: data.senderName || ref.senderName || '',
          title: data.title || ref.title || '',
          note: data.note || '',
          shopId: ref.shopId,
          shopName: data.shopName || '',
          color: WISH_COLORS[i % WISH_COLORS.length],
          items: (data.items || []).map((item: any) => ({
            name: item.name || '',
            price: item.price ?? null,
            frameIndex: item.frameIndex || 0,
            sectionId: item.sectionId || '',
            shopId: item.shopId || '',
            shopName: item.shopName || '',
            thumbnailUrl: item.thumbnailUrl || null,
            note: item.note || '',
            imagePixelX: item.imagePixelX || 0,
            imagePixelY: item.imagePixelY || 0,
            radius: item.radius || 30,
            point3D: item.point3D || null,
          })),
          visible: true,
        })
      } catch {
        // skip failed fetches
      }
    }

    set({ activeWishlists: loaded, loading: false })
  },

  toggleWishlistVisibility: (token: string) => {
    set((s) => ({
      activeWishlists: s.activeWishlists.map((wl) =>
        wl.token === token ? { ...wl, visible: !wl.visible } : wl
      ),
    }))
  },

  removeActiveWishlist: (token: string) => {
    removeActiveWishlistRef(token)
    removeWishlistToken(token)  // keep home page history in sync
    set((s) => ({
      activeWishlists: s.activeWishlists.filter((wl) => wl.token !== token),
    }))
  },

  dismissBanner: () => set({ bannerDismissed: true }),

  clearAll: () => set({ activeWishlists: [], bannerDismissed: true }),
}))
